import { useEffect, useState } from 'react';
import {
  Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay,
  FormControl, FormHelperText, FormLabel, Grid, Input, Select, Text, Textarea, useToast,
} from '@chakra-ui/react';
import {
  createMovement, createTransfer, updateMovement, uploadAttachments, errorMessage, todayIso,
} from 'api/administrador/finance';
import CategoryAutocomplete from './CategoryAutocomplete';
import { SegmentedButtons } from './ui';

const EMPTY = { type: 'egreso', date: '', amount: '', trm: '', category: null, concept: '', thirdParty: '', studentCode: '', note: '', to: '' };

/**
 * Nuevo movimiento o editar uno: ingreso, egreso o traslado entre cuentas (dos movimientos que no
 * suman como gasto). La categoría es opcional.
 */
export default function MovementDrawer({ isOpen, onClose, account, accounts, categories, movement, onSaved, onCreateCategory, initialType, allowTransfer = true }) {
  const toast = useToast();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!movement;
  const book = account?.book;
  const isCaja = book === 'caja_menor';
  const isTransfer = form.type === 'traslado';
  const isUsd = account?.currency === 'USD';

  useEffect(() => {
    if (!isOpen) return;
    setFiles([]);
    if (movement) {
      setForm({
        type: movement.type,
        date: movement.date,
        amount: String(movement.amount),
        trm: movement.trm ? String(movement.trm) : '',
        category: movement.category?.id || null,
        concept: movement.concept || '',
        thirdParty: movement.thirdParty || '',
        studentCode: movement.studentCode || '',
        note: movement.note || '',
        to: '',
      });
    } else {
      setForm({ ...EMPTY, type: initialType || 'egreso', date: todayIso() });
    }
  }, [isOpen, movement, initialType]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));
  const others = (accounts || []).filter((a) => a.id !== account?.id && a.active);

  async function save(another) {
    setSaving(true);
    try {
      let saved;
      if (isTransfer) {
        await createTransfer({ from: account.id, to: Number(form.to), date: form.date, amount: Number(form.amount), trm: form.trm ? Number(form.trm) : undefined, note: form.note });
      } else {
        const body = {
          account: account.id,
          type: form.type,
          date: form.date,
          amount: Number(form.amount),
          trm: isUsd ? Number(form.trm) : undefined,
          category: form.category,
          concept: form.concept,
          thirdParty: form.thirdParty,
          studentCode: isCaja ? form.studentCode : undefined,
          note: form.note,
        };
        if (isEdit && movement.isTransfer) {
          delete body.amount;
          delete body.type;
          delete body.account;
          delete body.trm;
        }
        saved = isEdit ? await updateMovement(movement.id, body) : await createMovement(body);
        if (files.length) saved = await uploadAttachments(saved.id, files);
      }
      toast({ status: 'success', title: isEdit ? 'Movimiento actualizado' : isTransfer ? 'Traslado registrado' : 'Movimiento registrado', duration: 3000 });
      onSaved?.();
      if (another && !isEdit) {
        setForm((f) => ({ ...EMPTY, type: f.type, date: f.date }));
        setFiles([]);
      } else {
        onClose();
      }
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e), duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  }

  const typeOptions = isEdit
    ? [{ value: form.type, label: form.type === 'ingreso' ? 'Ingreso' : 'Egreso' }]
    : [{ value: 'ingreso', label: 'Ingreso' }, { value: 'egreso', label: 'Egreso' }, ...(allowTransfer ? [{ value: 'traslado', label: 'Traslado' }] : [])];

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text fontSize="xl" fontWeight="600">{isEdit ? 'Editar movimiento' : 'Nuevo movimiento'}</Text>
          <Text fontSize="sm" color="gray.500" fontWeight="400">
            {isTransfer ? 'Entre cuentas · no suma como gasto' : `${isCaja ? 'Flujo de caja' : 'Bancos'} · ${account?.name || ''}`}
          </Text>
        </DrawerHeader>
        <DrawerBody display="flex" flexDirection="column" gap={5} py={5}>
          <FormControl>
            <FormLabel>Tipo</FormLabel>
            <SegmentedButtons size="md" ariaLabel="Tipo de movimiento" options={typeOptions} value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v, category: null }))} />
            {isTransfer && <FormHelperText>Crea un egreso en {account?.name} y un ingreso en la cuenta de destino.</FormHelperText>}
            {isEdit && movement?.isTransfer && <FormHelperText>Es un traslado: el monto y la cuenta no se editan. Si está mal, anúlalo y regístralo de nuevo.</FormHelperText>}
          </FormControl>

          <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
            <FormControl isRequired>
              <FormLabel htmlFor="fm-date">Fecha</FormLabel>
              <Input id="fm-date" type="date" max={todayIso()} value={form.date} onChange={set('date')} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel htmlFor="fm-amount">Monto {isUsd ? '(USD)' : ''}</FormLabel>
              <Input id="fm-amount" type="number" min="0" step={isUsd ? '0.01' : '1'} value={form.amount} onChange={set('amount')} isDisabled={isEdit && movement?.isTransfer} sx={{ fontVariantNumeric: 'tabular-nums' }} />
            </FormControl>
          </Grid>

          {(isUsd || (isTransfer && (others.find((a) => a.id === Number(form.to))?.currency === 'USD'))) && !(isEdit && movement?.isTransfer) && (
            <FormControl isRequired>
              <FormLabel htmlFor="fm-trm">TRM del día</FormLabel>
              <Input id="fm-trm" type="number" min="0" step="0.01" value={form.trm} onChange={set('trm')} />
              <FormHelperText>Con ella se convierte a pesos para el Resumen.</FormHelperText>
            </FormControl>
          )}

          {isTransfer ? (
            <FormControl isRequired>
              <FormLabel htmlFor="fm-to">Hacia</FormLabel>
              <Select id="fm-to" placeholder="Elige la cuenta de destino" value={form.to} onChange={set('to')}>
                {others.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' · USD' : ''}</option>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <FormControl>
                <FormLabel htmlFor="fm-category">Categoría <Text as="span" fontWeight="400" color="gray.500">(opcional)</Text></FormLabel>
                <CategoryAutocomplete id="fm-category" categories={categories} book={book} type={form.type} value={form.category} onChange={set('category')} onCreate={onCreateCategory} />
                <FormHelperText>Solo muestra las categorías activas de {isCaja ? 'Flujo de caja' : 'Bancos'}. Si no sabes cuál, déjala vacía y asígnala después.</FormHelperText>
              </FormControl>
              {isCaja && form.type === 'ingreso' && (
                <FormControl>
                  <FormLabel htmlFor="fm-student">Código del estudiante</FormLabel>
                  <Input id="fm-student" value={form.studentCode} onChange={set('studentCode')} />
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel htmlFor="fm-concept">{isCaja ? 'Detalle' : 'Concepto'}</FormLabel>
                <Input id="fm-concept" value={form.concept} onChange={set('concept')} maxLength={255} />
              </FormControl>
              {!isCaja && (
                <FormControl>
                  <FormLabel htmlFor="fm-third">Tercero <Text as="span" fontWeight="400" color="gray.500">(opcional)</Text></FormLabel>
                  <Input id="fm-third" value={form.thirdParty} onChange={set('thirdParty')} />
                  <FormHelperText>A quién se le pagó o quién consignó.</FormHelperText>
                </FormControl>
              )}
            </>
          )}

          <FormControl>
            <FormLabel htmlFor="fm-note">Nota interna <Text as="span" fontWeight="400" color="gray.500">(opcional)</Text></FormLabel>
            <Textarea id="fm-note" rows={2} value={form.note} onChange={set('note')} />
            <FormHelperText>Solo la ven Finanzas y el superadmin.</FormHelperText>
          </FormControl>

          {!isTransfer && (
            <FormControl>
              <FormLabel htmlFor="fm-files">Soporte <Text as="span" fontWeight="400" color="gray.500">(opcional)</Text></FormLabel>
              <Input id="fm-files" type="file" multiple accept="image/*,application/pdf" p={1} onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              {isEdit && movement?.attachments?.length > 0 && (
                <Box mt={2} fontSize="sm">
                  {movement.attachments.map((f) => (
                    <Text key={f.id} as="a" href={f.url} target="_blank" rel="noreferrer" color="purple.600" display="block">{f.name}</Text>
                  ))}
                </Box>
              )}
            </FormControl>
          )}
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px" gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {!isEdit && <Button variant="outline" onClick={() => save(true)} isLoading={saving}>Guardar y crear otro</Button>}
          <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={() => save(false)} isLoading={saving}>Guardar</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
